import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { DisclaimerArchiveButton } from "@/components/DisclaimerArchive";
import { AdminReo } from "@/components/realtor/AdminReo";

const on = (ap) => ["source", "email", "dial"].filter((k) => ap[k]).join(", ") || "off";

const Manager = ({ m, reload }) => {
  const setStatus = async (status) => {
    try { await api.put(`/admin/pm/${m.id}/status`, { status }); toast.success(`Account ${status}`); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="grid gap-2 border-t border-slate-100 py-3 text-sm md:grid-cols-[2fr_2fr_auto]" data-testid={`admin-pm-${m.id}`}>
      <div><div className="font-semibold text-slate-900">{m.name}</div><div className="text-xs text-slate-500">{m.email} · {m.phone || "no phone"}</div>
        <Badge className={`mt-1 border-0 ${m.status === "pending" ? "bg-amber-50 text-amber-800" : m.status === "suspended" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-800"}`} data-testid={`admin-pm-status-${m.id}`}>{m.status || "active"}</Badge>
        <Badge className={`ml-1 mt-1 border-0 ${m.disclaimer_signed ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid={`admin-pm-disclaimer-${m.id}`}>
          {m.disclaimer_signed ? `Disclaimer signed ${new Date(m.disclaimer_signed_at).toLocaleDateString()}` : "Disclaimer not signed yet"}</Badge></div>
      <div className="text-xs text-slate-600">
        <div className="font-semibold text-slate-800">{m.role === "realtor" ? "Realtor" : "Property manager"}</div>
        {m.role === "realtor" ? <>
          <div>FSBO leads: {m.leads.fsbo} · autopilots: {on(m.autopilot.fsbo)}</div>
          <div>Expired &amp; cancelled: {m.leads.expired} · autopilots: {on(m.autopilot.expired)}</div>
        </> : <>
          <div>Owner leads: {m.leads.owner} · autopilots: {on(m.autopilot.owner)}</div>
          <div>Elite Rentals: {m.leads.agent} · autopilots: {on(m.autopilot.agent)}</div>
        </>}
        <div>Area: {m.zips.join(", ") || "none"}{m.tcpa_signed_at ? ` · TCPA signed ${new Date(m.tcpa_signed_at).toLocaleDateString()}` : ""}</div>
      </div>
      <div className="flex gap-2">
        <DisclaimerArchiveButton u={m} />
        {m.status !== "active" && <Button size="sm" onClick={() => setStatus("active")} data-testid={`admin-pm-approve-${m.id}`}>Approve</Button>}
        {m.status !== "suspended" && <Button size="sm" variant="outline" onClick={() => setStatus("suspended")} data-testid={`admin-pm-suspend-${m.id}`}>Suspend</Button>}
      </div>
    </div>
  );
};

export default function AdminPM() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/pm/overview").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const toggle = async (patch, msg) => {
    try { await api.put("/admin/pm/settings", patch); toast.success(msg); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!d) return null;
  return (
    <div className="mx-auto max-w-6xl" data-testid="admin-pm-page">
      <h1 className="font-display text-3xl font-extrabold text-slate-900">Property Management &amp; Realtors</h1>
      <p className="mb-5 text-sm text-slate-600">Property manager and realtor accounts, their leads and autopilots.</p>
      <Card className="mb-5 flex items-center justify-between gap-4 p-6">
        <div><div className="font-semibold text-slate-900">Disable the dialing autopilot for all property managers and realtors</div>
          <div className="text-xs text-slate-500">Stops AI calls, texts and voicemails for every property manager and realtor. They can't turn it back on while this is on.</div></div>
        <Switch checked={!!d.dialing_disabled} onCheckedChange={(v) => toggle({ dialing_disabled: v }, v ? "Dialing autopilot turned off for all property managers" : "Property managers can turn dialing back on")} data-testid="admin-pm-disable-dialing" />
      </Card>
      <Card className="mb-5 flex items-center justify-between gap-4 p-6">
        <div><div className="font-semibold text-slate-900">Allow the "Call, text &amp; voicemail all now" button</div>
          <div className="text-xs text-slate-500">When off, property managers and realtors can't call, text and leave voicemails for all their leads at once.</div></div>
        <Switch checked={!d.dial_now_disabled} onCheckedChange={(v) => toggle({ dial_now_disabled: !v }, v ? "Call-all-now button turned on" : "Call-all-now button turned off")} data-testid="admin-pm-dial-now-switch" />
      </Card>
      <Card className="p-6">
        <h3 className="mb-1 font-display text-lg font-bold text-slate-900">Property managers &amp; realtors ({d.managers.length})</h3>
        <p className="mb-2 text-xs text-slate-500" data-testid="admin-pm-disclaimer-summary">
          Real estate associate disclaimer: {d.managers.filter((m) => m.disclaimer_signed).length} signed · {d.managers.filter((m) => !m.disclaimer_signed).length} still waiting (they're asked to sign at their next login)</p>
        {d.managers.map((m) => <Manager key={m.id} m={m} reload={load} />)}
        {!d.managers.length && <p className="text-sm text-slate-500" data-testid="admin-pm-empty">No accounts yet. They sign up from the Property Mgmt or Realtors tab on the login page.</p>}
      </Card>
      <AdminReo />
    </div>
  );
}
